import {
  Suspense,
  lazy,
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
  ChangeEventHandler,
  UIEventHandler,
  RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import download from 'downloadjs'
import { startsWith } from 'lodash-es'
import { useApolloClient } from '@apollo/client'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'

import { FILE_DOWNLOAD } from '@/graphql/mutations/Document'
import Icon from '@/ui/Icon'
import Text from '@/ui/Text'
import FlexRow from '@/ui/FlexRow'
import { b64toBlob, toggleFullscreen } from '@/helpers/general-helpers'
import { useEventListener, useLoggedInUser } from '@/hooks'
import {
  PDFViewerContainer,
  ScrollContainer,
  FullscreenButtonContainer,
  FullscreenButton,
  ActionsOverflow,
  DocumentActions,
  PageNumberWrapper,
  PageNumber,
  Overlay,
  DocumentLoader,
  Spinner,
} from './Styles'

const Document = lazy(() =>
  import('react-pdf').then(module => ({ default: module.Document })),
)

const Page = lazy(() =>
  import('react-pdf').then(module => ({ default: module.Page })),
)

interface Props {
  data?: any
  noPageHeader?: boolean
  height?: string
  minHeight?: number
  scrollPosition?: number
  loading?: boolean
  onDownload?: () => void
  getPageCount?: (value: number) => number
  scrollRef?: RefObject<HTMLDivElement>
  fileName?: string
  showInlinePageNumbers?: boolean
  storageId?: string
}

const PDFViewer = ({
  data,
  noPageHeader,
  height,
  minHeight,
  scrollPosition,
  loading,
  onDownload,
  getPageCount,
  scrollRef,
  fileName,
  showInlinePageNumbers,
  storageId,
}: Props) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const [toolbar, setToolbar] = useState(false)
  const [touchDevice, setTouchDevice] = useState(false)
  const [pdf, setPdf] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [fullscreen, setFullscreen] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(true)
  const [width, setWidth] = useState(0)
  const container = useRef<HTMLDivElement>(null)
  const overflowRef = useRef(null)
  const overflowContainer = scrollRef ?? overflowRef
  const { locale } = useLoggedInUser()
  const isTouchDevice = () => setTouchDevice(true)
  const showToolbar = () => setToolbar(!toolbar)

  // Constants
  const offsetWidth = container.current?.offsetWidth || 0

  const handlePDF = useCallback(() => {
    if (!data) {
      return
    }

    const regex =
      data.mimeType === 'application/octet-stream'
        ? /data:application\/octet-stream;base64,/gm
        : /data:application\/pdf;base64,/gm

    const filePath = data.htmlToPdf ? data.htmlToPdf.base64 : data.filePath
    const base64 = filePath.replace(regex, '')
    const blob = b64toBlob(base64, 'application/pdf', 512)
    const downloadUrl = URL.createObjectURL(blob)

    setPdf(downloadUrl)
  }, [data])

  useEventListener('resize', () => {
    if (offsetWidth !== undefined) {
      setWidth(scrollRef ? offsetWidth : offsetWidth - 10)
    }
  })

  useEffect(() => {
    handlePDF()

    window.addEventListener('touchstart', isTouchDevice)

    import('react-pdf').then(({ pdfjs }) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`
    })

    return () => {
      window.removeEventListener('touchstart', isTouchDevice)
    }
  }, [handlePDF])

  useEffect(() => {
    handlePDF()
  }, [data, handlePDF])

  useEffect(() => {
    if (loading) {
      setLoadingPdf(true)
      overflowContainer.current?.scrollTo(
        0,
        overflowContainer.current.scrollTop,
      )
    }
  }, [loading])

  const handleDocumentScroll: UIEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (numPages === null) return
      const pageSize = (
        document.querySelector(
          `.react-pdf__Page[data-page-number="1"]`,
        ) as HTMLDivElement
      )?.offsetHeight

      const currentPageNumber =
        Math.floor(
          ((event.target as Element)?.scrollTop + pageSize / 2) / pageSize,
        ) + 1

      if (currentPageNumber !== pageNumber && currentPageNumber <= numPages) {
        setPageNumber(currentPageNumber)
      }
    },
    [numPages, pageNumber],
  )

  useEffect(() => {
    if (scrollRef) {
      const scrollNode = scrollRef.current

      scrollNode?.addEventListener('scroll', () => handleDocumentScroll)

      return () => {
        scrollNode?.removeEventListener('scroll', () => handleDocumentScroll)
      }
    }
  }, [numPages, pageNumber])

  const handleDownload = async () => {
    if (onDownload) {
      return onDownload()
    }

    if (storageId) {
      const { data } = await client.mutate({
        mutation: FILE_DOWNLOAD,
        variables: {
          id: storageId,
        },
      })
      if (data == null) {
        throw new Error('File not found!')
      }

      const { base64, filename, mimetype }: any = data.FileDownload

      download(
        b64toBlob(
          base64.includes(',') ? base64.split(',')[1] : base64,
          mimetype,
        ),
        filename,
        mimetype,
      )
      return
    }

    if (data && !data?.mimeType) {
      const file = data.htmlToPdf ? data.htmlToPdf.base64 : data.filePath
      const blob = b64toBlob(file, 'application/pdf', 512)
      download(blob, data.fileName, 'application/pdf')
    } else {
      const newFilePath = !startsWith(data.filePath, 'data:')
        ? `data:application/pdf;base64,${data.filePath}`
        : data.filePath

      download(newFilePath, data.fileName, 'application/pdf')
    }
  }

  const documentRendered = () => {
    setLoadingPdf(false)
    if (scrollPosition) {
      overflowContainer.current?.scrollTo(0, scrollPosition)
    }
  }

  const onDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf.numPages)

    if (getPageCount) {
      getPageCount(pdf.numPages)
    }
  }

  const handlePageInput: ChangeEventHandler<HTMLInputElement> = e => {
    setPageNumber(parseInt(e.target.value))
  }

  const changePage = (input: string | number) => {
    const inputNumber = typeof input === 'string' ? parseInt(input) : input
    const page = Math.min(Math.max(inputNumber, 1), numPages || 0)

    const { offsetTop } = document.querySelector(
      `.react-pdf__Page[data-page-number="${page}"]`,
    ) as HTMLDivElement

    overflowContainer.current?.scrollTo(0, offsetTop)

    setPageNumber(page)
  }

  let pageWidth = container.current ? width || offsetWidth : 1000

  if (!scrollRef) {
    pageWidth -= 10
  }

  return (
    <PDFViewerContainer ref={container}>
      {!scrollRef && (
        <FullscreenButtonContainer>
          <FullscreenButton
            name={fullscreen ? 'icon-minimize' : 'icon-full-screen'}
            onClick={() => {
              setFullscreen(!fullscreen)
              toggleFullscreen(container)
            }}
          />
        </FullscreenButtonContainer>
      )}

      <ScrollContainer
        ref={overflowContainer}
        disabled={!!scrollRef}
        onMouseEnter={() => {
          if (!touchDevice) {
            showToolbar()
          }
        }}
        onMouseLeave={() => {
          if (!touchDevice && toolbar) {
            showToolbar()
          }
        }}
        onClick={() => {
          if (touchDevice) {
            showToolbar()
          }
        }}
        onScroll={e => handleDocumentScroll(e)}
        style={{
          height:
            fullscreen || noPageHeader
              ? '100vh'
              : window.innerWidth < 993 && !showInlinePageNumbers
              ? window.innerHeight - 75 + 'px'
              : height,
          minHeight,
        }}>
        {!showInlinePageNumbers && (
          <ActionsOverflow
            sticky={!!scrollRef}
            onClick={e => {
              if (toolbar && touchDevice) {
                e.stopPropagation()
              }
            }}>
            {overflowContainer.current && !!pdf && (
              <DocumentActions hide={!toolbar}>
                <FlexRow noMargin justifyContent="center">
                  {pageNumber > 1 ? (
                    <Icon
                      button
                      size="large"
                      onClick={() => changePage(1)}
                      name="icon-angle-double-down"
                      color="white"
                      style={{ transform: 'rotate(90deg)' }}
                    />
                  ) : (
                    <Icon
                      name="icon-angle-double-down"
                      size="large"
                      color="white"
                      style={{ opacity: '0.3', transform: 'rotate(90deg)' }}
                    />
                  )}
                  {pageNumber > 1 ? (
                    <Icon
                      button
                      size="large"
                      onClick={() => changePage(pageNumber - 1)}
                      name="icon-angle-down"
                      color="white"
                      style={{ transform: 'rotate(90deg)' }}
                    />
                  ) : (
                    <Icon
                      size="large"
                      name="icon-angle-down"
                      color="white"
                      style={{ opacity: '0.3', transform: 'rotate(90deg)' }}
                    />
                  )}
                  <PageNumberWrapper>
                    {locale === 'ja_JP' ? (
                      <>
                        {numPages}
                        {' / '}
                        <PageNumber
                          onBlur={e => changePage(e.target.value)}
                          onChange={e => handlePageInput(e)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const target = e.target as HTMLInputElement
                              target.blur()
                            }
                          }}
                          max={numPages || 0}
                          min={1}
                          value={pageNumber}
                        />
                      </>
                    ) : (
                      <>
                        {t('Page')}
                        <PageNumber
                          onBlur={e => changePage(e.target.value)}
                          onChange={e => handlePageInput(e)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const target = e.target as HTMLInputElement
                              target.blur()
                            }
                          }}
                          max={numPages || 0}
                          min={1}
                          value={pageNumber}
                        />{' '}
                        {t('of')} {numPages}
                      </>
                    )}
                  </PageNumberWrapper>
                  {numPages != null && pageNumber < numPages ? (
                    <Icon
                      button
                      size="large"
                      onClick={() => changePage(pageNumber + 1)}
                      name="icon-angle-down"
                      color="white"
                      style={{ transform: 'rotate(-90deg)' }}
                    />
                  ) : (
                    <Icon
                      size="large"
                      name="icon-angle-down"
                      color="white"
                      style={{ opacity: '0.3', transform: 'rotate(-90deg)' }}
                    />
                  )}
                  {numPages !== null && pageNumber < numPages ? (
                    <Icon
                      button
                      size="large"
                      onClick={() => changePage(numPages || 0)}
                      name="icon-angle-double-down"
                      color="white"
                      style={{ transform: 'rotate(-90deg)' }}
                    />
                  ) : (
                    <Icon
                      size="large"
                      name="icon-angle-double-down"
                      color="white"
                      style={{ opacity: '0.3', transform: 'rotate(-90deg)' }}
                    />
                  )}
                </FlexRow>

                <button
                  type="button"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={e => {
                    e.preventDefault()
                    handleDownload()
                  }}>
                  <span>{t('Download')}</span>
                  <Icon name="icon-download" style={{ marginLeft: '5px' }} />
                </button>
              </DocumentActions>
            )}
          </ActionsOverflow>
        )}

        <div
          style={{
            marginTop: !!scrollRef && !showInlinePageNumbers ? '-63px' : 0,
          }}>
          {loadingPdf && (
            <Overlay style={{ height, minHeight }}>
              <DocumentLoader>
                <Spinner />
              </DocumentLoader>
            </Overlay>
          )}

          <Suspense fallback={t('Loading')}>
            {Document && pdf && (
              <div style={loadingPdf ? { display: 'none' } : {}}>
                <Document
                  renderMode="canvas"
                  onLoadError={console.error}
                  file={pdf}
                  onLoadSuccess={onDocumentLoadSuccess}>
                  {Array.from(new Array(numPages), (el, index) => (
                    <div key={index} style={{ marginBottom: '20px' }}>
                      {showInlinePageNumbers && (
                        <FlexRow>
                          <Text.Span grey overflowEllipsis>
                            {index === 0 && fileName}
                          </Text.Span>
                          <Text.Span grey style={{ whiteSpace: 'nowrap' }}>
                            {t('Page')} {index + 1}/{numPages}
                          </Text.Span>
                        </FlexRow>
                      )}

                      {index === numPages || 0 - 1 ? (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          onRenderSuccess={documentRendered}
                          width={pageWidth}
                        />
                      ) : (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={pageWidth}
                        />
                      )}
                    </div>
                  ))}
                </Document>
              </div>
            )}
          </Suspense>
        </div>
      </ScrollContainer>
    </PDFViewerContainer>
  )
}

export default memo(PDFViewer)
